import { useMemo } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Fieldset,
  Grid,
  Group,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { Link } from "react-router-dom";
import type { LegacyReportReviewState } from "../../../types/legacyReportReview";
import { useLegacyReportEditor } from "../../../hooks/useLegacyReportEditor";
import { LegacyHistoricalReportPreview } from "../../pathologist-review/LegacyHistoricalReportPreview";
import LegacyReportOriginal from "./LegacyReportOriginal";
import LegacyReportHeaderFields from "./LegacyReportHeaderFields";
import LegacyReportSectionEditor from "./LegacyReportSectionEditor";
import LegacyReportSourceReview from "./LegacyReportSourceReview";
import LegacyReportAssetReview from "./LegacyReportAssetReview";
import LegacyReportComparison from "./LegacyReportComparison";
import LegacyReportApproval from "./LegacyReportApproval";
import LegacyReportDraftConflict from "./LegacyReportDraftConflict";
import LegacyReportPdfLink from "./LegacyReportPdfLink";
import LegacyReportCropModal from "./LegacyReportCropModal";

interface Props {
  initial: LegacyReportReviewState & {
    draft: NonNullable<LegacyReportReviewState["draft"]>;
  };
}

export default function LegacyReportWorkspace({ initial }: Props) {
  const editor = useLegacyReportEditor(initial);
  const { saved, draft, form } = editor;
  const readonly = useMemo(
    () => Boolean(saved.baseline || !saved.capabilities.canPrepare),
    [saved.baseline, saved.capabilities.canPrepare]
  );
  const approvalKey = useMemo(
    () =>
      `${draft.contentFingerprint}:${
        saved.latestComparison?.id || "uncompared"
      }`,
    [draft.contentFingerprint, saved.latestComparison?.id]
  );
  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <Badge
            color={saved.baseline ? "green" : editor.dirty ? "orange" : "blue"}
          >
            {saved.baseline
              ? "Approved historical baseline"
              : editor.dirty
              ? "Unsaved changes"
              : `Saved revision ${draft.revision}`}
          </Badge>
          <Text size="sm" c="dimmed">
            {draft.manifest.sourceFamily}
          </Text>
        </Group>
        {!saved.baseline && (
          <Group>
            <Button
              variant="default"
              onClick={editor.handlePreview}
              loading={editor.pending === "preview"}
            >
              Generate candidate PDF
            </Button>
            <Button
              onClick={editor.handleCompare}
              loading={editor.pending === "compare"}
            >
              Compare rendered report
            </Button>
          </Group>
        )}
      </Group>
      {saved.baseline && (
        <Alert color="green" title="Historical baseline approved">
          <Stack gap="xs">
            <Text size="sm">
              Approved {new Date(saved.baseline.approvedAt).toLocaleString()}.
              This reconstruction is immutable; new clinical changes use the
              report revision workflow.
            </Text>
            <Text size="xs">
              Baseline {saved.baseline.id} · native base version 0
            </Text>
            <Button
              component={Link}
              to={`/cases/${saved.caseId}`}
              variant="light"
            >
              Return to case
            </Button>
          </Stack>
        </Alert>
      )}
      {editor.message && (
        <Alert color="blue" role="status">
          {editor.message}
        </Alert>
      )}
      {editor.error && (
        <Alert color="red" title="Review action needs attention">
          <Stack gap="sm">
            <Text size="sm">{editor.error.message}</Text>
            {editor.error.status === 409 && (
              <Button
                onClick={editor.handleGetLatest}
                loading={editor.pending === "refresh"}
                variant="light"
              >
                Compare with saved reconstruction
              </Button>
            )}
          </Stack>
        </Alert>
      )}
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, xl: 6 }}>
          <LegacyReportOriginal source={saved.source} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, xl: 6 }}>
          <Paper withBorder p="md">
            <Stack gap="sm">
              <Group justify="space-between">
                <Title order={2}>Reconstructed report</Title>
                <Badge
                  color={editor.rendered ? "green" : "orange"}
                  variant="light"
                >
                  {editor.rendered
                    ? `${editor.rendered.pageCount} pages rendered`
                    : editor.renderFailed
                    ? "Rendering unavailable"
                    : "Rendering candidate"}
                </Badge>
              </Group>
              {editor.dirty && (
                <Alert color="orange">
                  This preview shows the saved revision. Save your text and
                  source-review changes to update it.
                </Alert>
              )}
              <ScrollArea
                h="75vh"
                type="auto"
                aria-label="Reconstructed report pages"
              >
                <LegacyHistoricalReportPreview
                  key={`${draft.contentFingerprint}:${editor.renderAttempt}`}
                  caseId={saved.caseId}
                  documentViewData={draft.candidate.documentViewData}
                  onReady={editor.handleRendered}
                  onPending={editor.handleRenderPending}
                  onError={editor.handleRenderError}
                />
              </ScrollArea>
              {editor.renderFailed && (
                <Button variant="light" onClick={editor.handleRetryRender}>
                  Retry candidate rendering
                </Button>
              )}
              {saved.latestPreview && (
                <LegacyReportPdfLink
                  key={saved.latestPreview.id}
                  contentUrl={saved.latestPreview.contentUrl}
                />
              )}
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>
      <form onSubmit={editor.handleSave} noValidate>
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={2}>Literal content and source review</Title>
            {!readonly && (
              <Button type="submit" loading={editor.pending === "save"}>
                Save draft
              </Button>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            Historical identity is frozen separately from the current patient
            record. Preserve printed wording, dates, and attribution.
          </Text>
          <Fieldset disabled={Boolean(editor.pending)} variant="unstyled">
            <Stack gap="lg">
              <LegacyReportHeaderFields form={form} readOnly={readonly} />
              <Accordion multiple defaultValue={["text", "review"]}>
                <Accordion.Item value="text">
                  <Accordion.Control>
                    Literal sections and source evidence
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      {form.values.sections.map((section, index) => (
                        <LegacyReportSectionEditor
                          key={section.id}
                          index={index}
                          form={form}
                          readOnly={readonly}
                          evidence={draft.manifest.blocks.find(
                            (block) => block.id === section.id
                          )}
                        />
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
                <Accordion.Item value="review">
                  <Accordion.Control>
                    Signatures and unresolved source findings
                  </Accordion.Control>
                  <Accordion.Panel>
                    <LegacyReportSourceReview
                      form={form}
                      manifest={draft.manifest}
                      readOnly={readonly}
                    />
                  </Accordion.Panel>
                </Accordion.Item>
                <Accordion.Item value="graphics">
                  <Accordion.Control>
                    Preserved graphics and source page boundaries (
                    {draft.assets.length})
                  </Accordion.Control>
                  <Accordion.Panel>
                    <SimpleGrid cols={{ base: 1, lg: 2 }}>
                      {draft.assets.map((asset) => (
                        <LegacyReportAssetReview
                          key={asset.id}
                          asset={asset}
                          evidence={draft.manifest.assets.find(
                            (entry) => entry.id === asset.id
                          )}
                          form={form}
                          readOnly={readonly}
                        />
                      ))}
                    </SimpleGrid>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>
          </Fieldset>
          {!readonly && (
            <Button type="submit" loading={editor.pending === "save"} fullWidth>
              Save draft
            </Button>
          )}
        </Stack>
      </form>
      {!readonly && (
        <LegacyReportCropModal
          manifest={draft.manifest}
          draft={draft.data}
          assets={draft.assets}
          dirty={editor.dirty}
          onSave={editor.handleCrop}
        />
      )}
      <LegacyReportComparison comparison={saved.latestComparison} />
      {!saved.baseline && (
        <Paper withBorder p="md">
          <LegacyReportApproval
            key={approvalKey}
            blockers={editor.blockers}
            findings={saved.latestComparison?.result.discrepancies || []}
            pending={editor.pending === "approve"}
            onApprove={editor.handleApprove}
          />
        </Paper>
      )}
      {editor.latest && (
        <LegacyReportDraftConflict
          latest={editor.latest}
          current={form.values}
          mayRetainEdits={editor.mayRetainEdits}
          onClose={editor.handleCloseConflict}
          onLoad={editor.handleLoadLatest}
          onKeep={editor.handleKeepEdits}
        />
      )}
    </Stack>
  );
}
